import { identity } from 'lodash-es';
import { type ConditionalKeys } from 'type-fest';

import { getValue } from './object.helpers';
import { isDefined } from './validation.helpers';

export function compareByDateFactory<T>(
  key:
    | ConditionalKeys<T, Date | string | number>
    | ((item: T) => Date | string) = identity,
  direction: 'asc' | 'desc' = 'asc',
): (a: T, b: T) => number {
  return (a, b) => {
    const ak = getValue(a, key);
    const bk = getValue(b, key);

    if (!isDefined(ak) && !isDefined(bk)) {
      return 0;
    }

    if (!ak) {
      return direction === 'asc' ? -1 : 1;
    }

    if (!bk) {
      return direction === 'asc' ? 1 : -1;
    }

    return (
      (new Date(ak).valueOf() - new Date(bk).valueOf()) *
      (direction === 'asc' ? 1 : -1)
    );
  };
}

export function compareByAmountFactory<T>(
  key: ConditionalKeys<T, number> | ((item: T) => number) = identity,
  direction: 'asc' | 'desc' = 'asc',
): (a: T, b: T) => number {
  return (a, b): number => {
    const ak = getValue(a, key);
    const bk = getValue(b, key);

    if (ak === bk) {
      return 0;
    }

    return (
      ((typeof ak === 'number' ? ak : -1) -
        (typeof bk === 'number' ? bk : -1)) *
      (direction === 'asc' ? 1 : -1)
    );
  };
}

/**
 * direction 'asc' => truthy values first
 * direction 'desc' => falsy values first (it includes null or undefined)
 */
export function compareByBooleanFactory<T>(
  key: ConditionalKeys<T, boolean> | ((item: T) => boolean) = identity,
  direction: 'asc' | 'desc' = 'asc',
): (a: T, b: T) => number {
  return (a, b) => {
    const ak = getValue(a, key) ? -1 : 1;
    const bk = getValue(b, key) ? -1 : 1;

    return (ak - bk) * (direction === 'asc' ? 1 : -1);
  };
}

export function composeCompare<T>(
  compareFunctions: readonly ((a: T, b: T) => number)[],
): (a: T, b: T) => number {
  return (a, b): number => {
    for (const compareFunction of compareFunctions) {
      const res = compareFunction(a, b);

      if (res) {
        return res;
      }
    }

    return 0;
  };
}
